<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core'
  import { IconClose } from '@gat-ship/icons'
  import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogPortal,
    useForwardPropsEmits
  } from 'reka-ui'
  import type { HTMLAttributes } from 'vue'
  import { cn } from '../../../lib/utils'
  import SheetOverlay from './SheetOverlay.vue'
  import { type SheetVariants, sheetVariants } from './sheet-variants'

  interface SheetContentProps extends DialogContentProps {
    class?: HTMLAttributes['class']
    side?: SheetVariants['side']
    /** Default false */
    closeOnOutsideClick?: boolean
    hideCloseButton?: boolean
    hideOverlay?: boolean
    overlayZIndex?: number
  }

  defineOptions({
    inheritAttrs: false
  })

  const props = withDefaults(defineProps<SheetContentProps>(), {
    side: 'right',
    overlayZIndex: 50
  })
  const emits = defineEmits<
    DialogContentEmits & { 'click:close': [event: MouseEvent] }
  >()

  const delegatedProps = reactiveOmit(props, 'class', 'side')

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  function onOutsideClick(e: Event) {
    if (!props.closeOnOutsideClick) {
      e.preventDefault()
    }
  }
</script>

<template>
  <DialogPortal>
    <SheetOverlay
      v-if="!hideOverlay"
      class="fixed inset-0 bg-black/15 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      :style="{ zIndex: overlayZIndex }"
      @click.prevent
    />
    <DialogContent
      data-slot="sheet-content"
      :class="
        cn(
          sheetVariants({ side }),
          'pt-safe-area pb-safe-area pl-safe-area pr-safe-area bg-background fixed gap-4 p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
          props.class
        )
      "
      v-bind="{ ...forwarded, ...$attrs }"
      :style="{ zIndex: overlayZIndex }"
      :aria-describedby="undefined"
      @interact-outside="onOutsideClick($event)"
    >
      <slot />

      <DialogClose
        v-if="!hideCloseButton"
        class="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary rounded-xs focus:outline-hidden absolute right-4 top-4 opacity-70 transition-opacity hover:cursor-pointer hover:opacity-100 focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
        @click="emits('click:close', $event)"
      >
        <icon-close class="size-4" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
